import React from 'react';
import PlayIcon from '@assets/icons/play.svg';
import { BaseButton, BaseSpin, ChevronToggler } from '@components';
import { TEST_IDS } from '@constants/testIds.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import { ROUTES } from '@router/constants.ts';
import { getFullUserInfo } from '@routes/___shared___/user.slice.ts';
import { ELEMENT_ID_SEARCH_PARAM } from '@routes/sidebar/learning/___pages___/learning-overview/___shared___/constants.ts';
import { NOTIFICATIONS_VIEW_SCROLLABLE_SELECTOR as SCROLLABLE_SELECTOR } from '@routes/sidebar/notifications/___shared___/constants.ts';
import { isAssessorOpenNotification } from '@routes/sidebar/notifications/___shared___/helpers.ts';
import type { NotificationDetails } from '@routes/sidebar/notifications/api/notifications.api.ts';
import {
    setScrollTopPosition,
    toggleReadNotification,
} from '@routes/sidebar/notifications/model/notifications.slice.ts';
import {
    SHARED_CARD_ACTIONS_MARK_READ,
    SHARED_CARD_ACTIONS_MARK_UNREAD,
    SHARED_CARD_ACTIONS_OPEN,
} from '@routes/sidebar/notifications/ui/card-actions/constants.ts';
import {
    CardActionsButtons,
    CardActionsWrapper,
    DetailsToggler,
} from '@routes/sidebar/notifications/ui/card-actions/styled.tsx';
import { ButtonRead } from '@routes/sidebar/notifications/ui/notification-card/styled.tsx';
import { getCurrentScrollTopPosition } from '@utils/scroll.ts';
import { translateItem } from '@utils/translations.ts';

interface Props {
    note: NotificationDetails;
    isNotifyHidden: boolean;
    setIsNotifyHidden: (isNotifyHidden: boolean) => void;
}

const CardActions: React.FC<Props> = ({ note, isNotifyHidden, setIsNotifyHidden }) => {
    const dispatch = useAppDispatch();
    const navigate = useCustomNavigate();

    const updatingReadStateId = useAppSelector((state) => state.notifications.updatingReadStateId);
    const isUpdatingReadState = updatingReadStateId === note.id;
    const { hasOpenAction, pathwayId, elementId, notificationType } = note;

    const updateNotificationReadState = () => {
        void dispatch(toggleReadNotification(note.id))
            .then(() => {
                void dispatch(getFullUserInfo());
            })
            .catch(() => {});
    };

    const markNotificationAsRead = () => {
        if (note.wasRead) return;

        updateNotificationReadState();
    };

    const handleClick = () => {
        markNotificationAsRead();

        if (isAssessorOpenNotification(notificationType)) {
            navigate(`/${ROUTES.SIDEBAR.ADMIN.ROOT}/${ROUTES.SIDEBAR.ADMIN.ASSESSOR.ROOT}`, { absolute: true });
            return;
        }

        const pathwayRoute = `${ROUTES.SIDEBAR.LEARNING}/${pathwayId}`;
        const isTaskNotification = pathwayId !== null && elementId !== null;

        navigate(isTaskNotification ? `${pathwayRoute}?${ELEMENT_ID_SEARCH_PARAM}=${elementId}` : pathwayRoute);
    };

    const toggleRead = () => {
        dispatch(setScrollTopPosition(getCurrentScrollTopPosition(SCROLLABLE_SELECTOR)));
        updateNotificationReadState();
    };

    return (
        <CardActionsWrapper>
            <CardActionsButtons>
                {!!hasOpenAction && (
                    <BaseButton
                        className={'learning-action-btn'}
                        iconPlacement="end"
                        icon={<PlayIcon />}
                        onClick={handleClick}
                        data-cy={TEST_IDS.NOTIFICATIONS.NOTIFICATION_OPEN_BUTTON}
                    >
                        {translateItem(SHARED_CARD_ACTIONS_OPEN)}
                    </BaseButton>
                )}
                {isUpdatingReadState && updatingReadStateId === note.id ? (
                    <BaseSpin size={'small'} />
                ) : (
                    <ButtonRead onClick={toggleRead} data-cy={TEST_IDS.NOTIFICATIONS.NOTIFICATION_READ_TOGGLE}>
                        {note.wasRead
                            ? translateItem(SHARED_CARD_ACTIONS_MARK_UNREAD)
                            : translateItem(SHARED_CARD_ACTIONS_MARK_READ)}
                    </ButtonRead>
                )}
            </CardActionsButtons>
            <DetailsToggler data-cy={TEST_IDS.NOTIFICATIONS.NOTIFICATION_DETAILS_TOGGLER}>
                <ChevronToggler key={note.id} onChangeAction={() => setIsNotifyHidden(!isNotifyHidden)} />
            </DetailsToggler>
        </CardActionsWrapper>
    );
};

export default CardActions;
